/* eslint-disable string-to-lingui/missing-lingui-transformation */
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { OpenAPI } from '@om1/falcon-api/codegen/client/core/OpenAPI'
import { CohortCreateDTO } from '@om1/falcon-api/codegen/client/models/CohortCreateDTO'
import { QueryDTO_Input } from '@om1/falcon-api/codegen/client/models/QueryDTO_Input'
import { useCohort } from '@om1/falcon-api/codegen/hooks/useCohort'
import { useCreateCohort } from '@om1/falcon-api/codegen/hooks/useExplorerCohorts'
import { falconApiConfig } from '@om1/falcon-api/falcon-api-config'
import { useUserPermissions } from '@om1/platform-authentication/auth0/hooks/useUserPermissions'
import { PlatformPermissions } from '@om1/platform-utils'
import { useCallback, useEffect, useState } from 'react'
import ReactJson from 'react-json-view'
import { useHistory } from 'react-router-dom'
import { EditBlockDialog } from '../components/query/dialogs/EditBlockDialog'
import { Query } from '../components/query/Query'
import { NodeTypes } from '../components/query/QueryBlock'
import { SelectedBlockProps } from './ChatView'

export type AssistantQueryProps = {
    id: string
    updateCohortCount?: number
} & SelectedBlockProps

export const AssistantQuery = (props: AssistantQueryProps) => {
    const { updateCohortCount = 0 } = props
    const [editBlock, setEditBlock] = useState<{ filter: NodeTypes; index: number | undefined } | null>(null)
    const { cohort, isRefreshing, refetchCohort, findBlock } = useCohort({ cohortId: props.id as string })
    const [showQueryComponent, setShowQueryComponent] = useState(true)
    const { permissions } = useUserPermissions()
    const [openSaveAs, setOpenSaveAs] = useState(false)
    const [newCohortName, setNewCohortName] = useState('')
    const createCohort = useCreateCohort()
    const history = useHistory()
    const navigate = history.push

    useEffect(() => {
        if (updateCohortCount > 0) {
            refetchCohort()
        }
    }, [refetchCohort, updateCohortCount])

    const toggleQueryView = () => {
        if (cohort?.query) {
            setShowQueryComponent((prev) => !prev)
        }
    }

    const onBlockClick = useCallback(
        (id: number, _qualifierIndex?: number) => {
            const foundBlock = findBlock(id)
            if (foundBlock) {
                props.setSelectedBlock && props.setSelectedBlock(foundBlock)
            }
        },
        [findBlock, props]
    )

    useEffect(() => {
        if (props.selectedBlock?.id) {
            onBlockClick(props.selectedBlock.id)
        }
    }, [onBlockClick, props])

    const hasValidCount = typeof cohort?.cohortSize === 'number' && !Number.isNaN(cohort?.cohortSize)
    const canSave = Boolean(cohort?.query && hasValidCount && !isRefreshing)

    const handleSaveAs = () => {
        if (!cohort?.query || !newCohortName.trim()) return
        const newCohortData: CohortCreateDTO = {
            name: newCohortName.trim(),
            query: cohort.query as QueryDTO_Input,
            analyticsDatasetId: String(cohort.analyticsDataset.id),
            isPrivate: false
        }
        createCohort.mutate(newCohortData, {
            onSuccess: async (data) => {
                try {
                    // Kick off size update for the newly created cohort and await task completion via websocket
                    const response = await fetch(`${falconApiConfig.falconApiUrl}/explorer/tasks/cohort/${data.id}/update_size`, {
                        headers: { Authorization: `Bearer ${String(OpenAPI.TOKEN)}` }
                    })
                    const result = await response.json()
                    if (result?.taskId) {
                        await new Promise<void>((resolve) => {
                            const websocketUrl = `${falconApiConfig.falconApiUrl?.replace(new RegExp(/^http/), 'ws')}/tasks/ws/${result.taskId}`
                            const ws = new WebSocket(websocketUrl)
                            ws.onopen = () => {
                                ws.send(JSON.stringify({ token: String(OpenAPI.TOKEN) }))
                            }
                            ws.onmessage = (event) => {
                                const message = JSON.parse(event.data)
                                if (message.status?.toLowerCase() === 'success') {
                                    ws.close()
                                    resolve()
                                }
                            }
                            ws.onerror = () => {
                                try {
                                    ws.close()
                                } catch (e) {
                                    console.error('Error closing websocket:', e)
                                }
                                resolve()
                            }
                        })
                    }
                } catch (e) {
                    // Best-effort: ignore and continue navigation
                    // eslint-disable-next-line no-console
                    console.warn('Failed to pre-refresh cohort size, continuing to navigate', e)
                }
                setOpenSaveAs(false)
                navigate(`/cohort-builder/custom/${data.id}/edit`)
            },
            onError: (error) => {
                console.error('Error creating cohort:', error)
            }
        })
    }

    return (
        <div style={{ padding: '1rem', height: '100%', overflow: 'scroll' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div
                    style={{
                        minWidth: '200px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: '6px',
                        paddingTop: '2px',
                        paddingBottom: '16px'
                    }}
                >
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '2px' }}>PATIENT COUNT</div>
                    {isRefreshing ? (
                        <CircularProgress color='inherit' size='1.5em' />
                    ) : (
                        <div style={{ fontSize: '26px', fontWeight: 'bold', letterSpacing: '0.5px', color: 'rgb(16, 50, 117)' }}>
                            {cohort?.cohortSize?.toLocaleString() || '-'}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', height: '100%' }}>
                    <Button variant='outlined' size='medium' startIcon={<SaveAsIcon />} disabled={!canSave} onClick={() => setOpenSaveAs(true)}>
                        Save As
                    </Button>
                </div>
                {permissions.includes(PlatformPermissions.ACCESS_ADVANCED_ASSISTANT) && (
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <Button variant='outlined' size='medium' onClick={toggleQueryView} disabled={!cohort?.query}>
                            {showQueryComponent ? 'Show Raw JSON' : 'Show QueryBlock'}
                        </Button>
                    </div>
                )}
            </div>
            {showQueryComponent
                ? cohort?.query && <Query {...props} query={cohort.query} setEditBlock={setEditBlock} onBlockClick={onBlockClick} isBase />
                : cohort?.query && <ReactJson src={JSON.parse(JSON.stringify(cohort.query))} style={{ marginBottom: '25px' }} />}
            {editBlock && <EditBlockDialog editBlock={editBlock} onClose={() => setEditBlock(null)} />}

            <Dialog
                open={openSaveAs}
                onClose={() => {
                    setOpenSaveAs(false)
                }}
            >
                <DialogTitle>Save Cohort</DialogTitle>
                <DialogContent style={{ width: '400px', height: '100px' }}>
                    <TextField
                        autoFocus
                        margin='dense'
                        label='Cohort Name'
                        type='text'
                        fullWidth
                        value={newCohortName}
                        onChange={(e) => setNewCohortName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpenSaveAs(false)
                        }}
                        color='primary'
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSaveAs} color='primary' disabled={!newCohortName.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
